/**
 * The investor's realised capital gains, by financial year.
 *
 * NO NEW ENDPOINT. The rows arrive on `investor_reports.capital_gains` inside
 * the `reports_by_user_uuid` response the dashboard already loads, as a column
 * list plus positional rows — `buildCapitalGainStatementRows` maps them.
 *
 * This reports Fintech Primitives' own realised-gain fields. It does not
 * compute tax, classify STCG against LTCG, or apply a rate, and it only covers
 * units actually sold or switched out through SMEST — not unrealised gains on
 * holdings still owned. The downloaded statement says so on its face.
 */

import React, { useMemo, useState } from "react";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  buildCapitalGainStatementRows,
  filterCapitalGainsByFinancialYear,
  getCapitalGainFinancialYears,
} from "../../Helpers/mfCapitalGainsStatement";
import type {
  CapitalGainStatementRow,
  CapitalGainsReportTable,
} from "../../Helpers/mfCapitalGainsStatement";
import type { MFRecord } from "../../Api/mfAdminInvestments";
import styles from "../../pages/MutualFunds/user-investments-dashboard.module.css";

const InlineLoader = () => <span className={styles.inlineSpinner} aria-hidden="true" />;

const currency = (value: number | null, decimals = 2) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: decimals,
      }).format(value);

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const sum = (rows: CapitalGainStatementRow[], pick: (row: CapitalGainStatementRow) => number | null) =>
  rows.reduce((total, row) => total + (pick(row) || 0), 0);

/** `investor_reports.capital_gains` as the report API shapes it. */
const toReportTable = (reports: MFRecord | null): CapitalGainsReportTable | null => {
  const report = reports?.investor_reports?.capital_gains;
  const columns = report?.data?.columns;
  if (!Array.isArray(columns) || !columns.length) return null;
  return {
    columns,
    rows: Array.isArray(report?.data?.rows) ? report.data.rows : [],
  };
};

export interface CapitalGainsPanelProps {
  reports: MFRecord | null;
  investorName: string;
  pan?: string | null;
  accountRef?: string | null;
}

const CapitalGainsPanel: React.FC<CapitalGainsPanelProps> = ({
  reports,
  investorName,
  pan,
  accountRef,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const rows = useMemo(
    () => buildCapitalGainStatementRows(toReportTable(reports)),
    [reports]
  );
  const years = useMemo(() => getCapitalGainFinancialYears(rows), [rows]);
  const [yearKey, setYearKey] = useState("");

  const activeYear = years.find((year) => year.key === yearKey) || years[0] || null;
  const yearRows = useMemo(
    () => (activeYear ? filterCapitalGainsByFinancialYear(rows, activeYear.key) : []),
    [rows, activeYear]
  );

  const download = async () => {
    if (!activeYear) return;
    setDownloading(true);
    setError("");
    try {
      // jsPDF plus the embedded Jura weights — pulled only when asked for.
      const { downloadCapitalGainsPdf } = await import("../../Helpers/mfCapitalGainsPdf");
      downloadCapitalGainsPdf({
        investorName,
        pan: pan || null,
        accountRef: accountRef || null,
        financialYearLabel: activeYear.label,
        rows: yearRows,
      });
    } catch (downloadError) {
      setError(
        (downloadError as Error)?.message ||
          "The capital gains statement could not be built."
      );
    } finally {
      setDownloading(false);
    }
  };

  if (!rows.length) {
    return (
      <div className={styles.empty}>
        <div className={styles.emptyIcon}>
          <DownloadIcon />
        </div>
        No realised capital gains were reported for this investor. Gains appear
        here once units have been sold or switched out.
      </div>
    );
  }

  return (
    <>
      <div className={styles.sectionHeader}>
        <div>
          <h2>Realised capital gains</h2>
          <p>
            Units actually sold or switched out through SMEST. Unrealised gains
            on holdings still owned are not included, and nothing here is a tax
            computation.
          </p>
        </div>
        <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
          <select
            className={styles.select}
            style={{ width: "auto" }}
            value={activeYear ? activeYear.key : ""}
            onChange={(event) => setYearKey(event.target.value)}
          >
            {years.map((year) => (
              <option key={year.key} value={year.key}>
                {year.label}
              </option>
            ))}
          </select>
          <button
            className={styles.kycButton}
            disabled={downloading || !yearRows.length}
            aria-busy={downloading || undefined}
            onClick={download}
          >
            {downloading ? <InlineLoader /> : <DownloadIcon fontSize="small" />}
            {downloading ? "Building…" : "Download statement"}
          </button>
        </div>
      </div>

      {error && <div className={styles.error} style={{ marginBottom: 14 }}>{error}</div>}

      <section className={styles.metricGrid} style={{ marginTop: 0 }}>
        <div className={styles.metricCard}>
          <div className={styles.metricTop}>
            <span>Redemption value</span>
          </div>
          <div className={styles.metricValue}>
            {currency(sum(yearRows, (row) => row.amount), 0)}
          </div>
          <div className={styles.metricHint}>{yearRows.length} transactions</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricTop}>
            <span>Actual gain</span>
          </div>
          <div
            className={`${styles.metricValue} ${
              sum(yearRows, (row) => row.actualGain) >= 0 ? styles.positive : styles.negative
            }`}
          >
            {currency(sum(yearRows, (row) => row.actualGain), 0)}
          </div>
          <div className={styles.metricHint}>Before indexation</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricTop}>
            <span>Taxable gain</span>
          </div>
          <div className={styles.metricValue}>
            {currency(sum(yearRows, (row) => row.taxableGain), 0)}
          </div>
          <div className={styles.metricHint}>As reported by the registrar</div>
        </div>
        <div className={styles.metricCard}>
          <div className={styles.metricTop}>
            <span>Indexed gain</span>
          </div>
          <div className={styles.metricValue}>
            {currency(sum(yearRows, (row) => row.indexedCapitalGain), 0)}
          </div>
          <div className={styles.metricHint}>Where indexation applies</div>
        </div>
      </section>

      <div className={styles.tableWrap}>
        <table className={styles.table} style={{ minWidth: 1050 }}>
          <thead>
            <tr>
              <th>Scheme</th>
              <th>Type</th>
              <th>Traded on</th>
              <th>Units</th>
              <th>Amount</th>
              <th>Held</th>
              <th>Actual gain</th>
              <th>Taxable gain</th>
              <th>Indexed gain</th>
            </tr>
          </thead>
          <tbody>
            {yearRows.map((row, index) => (
              <tr key={`${row.folioNumber || ""}-${row.isin || ""}-${row.tradedOn || ""}-${index}`}>
                <td>
                  <div className={styles.fundName}>{row.schemeName}</div>
                  <div className={styles.fundMeta}>
                    {row.folioNumber ? `Folio ${row.folioNumber}` : ""}
                    {row.isin ? ` · ${row.isin}` : ""}
                    {row.grandfathering
                      ? ` · grandfathered at ${currency(row.grandfatheringNav)}`
                      : ""}
                  </div>
                </td>
                <td>{row.transactionType || "—"}</td>
                <td>{formatDate(row.tradedOn)}</td>
                <td>
                  {row.units == null
                    ? "—"
                    : row.units.toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                </td>
                <td>{currency(row.amount)}</td>
                <td>{row.daysHeld == null ? "—" : `${row.daysHeld} days`}</td>
                <td className={(row.actualGain || 0) >= 0 ? styles.positive : styles.negative}>
                  {currency(row.actualGain)}
                </td>
                <td>{currency(row.taxableGain)}</td>
                <td>{currency(row.indexedCapitalGain)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.warning} style={{ marginTop: 16 }}>
        This shows the taxable gain on units sold or switched out through SMEST
        Capital only. It is not a tax computation or advice — refer the investor
        to their Form 26AS/AIS or a tax advisor for actual liability.
      </div>
    </>
  );
};

export default CapitalGainsPanel;
